import React from "react";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import { Box, Card, Typography as MuiTypography, IconButton, Menu, MenuItem } from "@material-ui/core";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import DeleteIcon from "@material-ui/icons/Delete";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import moment from "moment";

interface Video {
  id: string;
  title?: string;
  description?: string;
  youtubeUrl?: string;
  youtubeVideoId?: string;
  fileUrl?: string;
  thumbnail?: string;
  duration?: string;
  uploadedAt: string;
  uploadedBy?: string;
}

interface VideoCardProps {
  video: Video;
  onClick: () => void;
  onDelete?: (videoId: string) => void;
  showDeleteButton?: boolean;
}

// Styled Components
const StyledCard = styled(Card)(() => ({
  position: "relative",
  borderRadius: "12px",
  overflow: "hidden",
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    "& $PlayOverlay": {
      opacity: 1,
    },
  },
}));

const ThumbnailContainer = styled(Box)({
  position: "relative",
  width: "100%",
  paddingTop: "56.25%", // 16:9 aspect ratio
  background: "#000",
  overflow: "hidden",
});

const Thumbnail = styled("img")({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

const PlayOverlay = styled(Box)(() => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  opacity: 0,
  transition: "opacity 0.3s ease",
}));

const PlayButton = styled(IconButton)(({ theme }: IStyledArguments) => ({
  background: theme.palette.primary.main,
  color: "white",
  width: "64px",
  height: "64px",
  "&:hover": {
    background: theme.palette.primary.dark,
    transform: "scale(1.1)",
  },
}));

const DurationBadge = styled(Box)(() => ({
  position: "absolute",
  bottom: "8px",
  right: "8px",
  background: "rgba(0, 0, 0, 0.8)",
  color: "white",
  padding: "4px 8px",
  borderRadius: "4px",
  fontSize: "0.75rem",
  fontWeight: 600,
}));

const MenuButton = styled(IconButton)(() => ({
  position: "absolute",
  top: "4px",
  right: "4px",
  background: "rgba(0, 0, 0, 0.6)",
  color: "white",
  width: "32px",
  height: "32px",
  padding: "4px",
  zIndex: 2,
  "&:hover": {
    background: "rgba(0, 0, 0, 0.8)",
  },
}));

const VideoInfo = styled(Box)(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(2),
  background: theme.palette.extra.default,
}));

const VideoTitle = styled(MuiTypography)({
  fontWeight: 600,
  fontSize: "0.95rem",
  marginBottom: "4px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
});

const VideoMeta = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "0.85rem",
  color: "#666",
});

const MetaDot = styled("span")({
  fontSize: "0.5rem",
});

/**
 * Formats duration from seconds to MM:SS or HH:MM:SS
 */
const formatDuration = (duration?: string): string => {
  if (!duration) return "0:00";
  
  const seconds = parseInt(duration, 10);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

/**
 * Formats uploaded date to relative time
 */
const formatUploadedDate = (dateString: string): string => {
  return moment(dateString).fromNow();
};

/**
 * Gets the appropriate thumbnail URL for a video
 */
const getThumbnailUrl = (video: Video): string => {
  // If video has explicit thumbnail, use it
  if (video.thumbnail) {
    return video.thumbnail;
  }
  
  // For YouTube videos, use YouTube thumbnail
  if (video.youtubeVideoId) {
    return `https://img.youtube.com/vi/${video.youtubeVideoId}/maxresdefault.jpg`;
  }
  
  // For REST videos, use a default placeholder (using stadium image as placeholder)
  return '/images/stadium.jpg';
};

/**
 * Gets the fallback thumbnail URL for a video
 */
const getFallbackThumbnailUrl = (video: Video): string => {
  if (video.youtubeVideoId) {
    return `https://img.youtube.com/vi/${video.youtubeVideoId}/hqdefault.jpg`;
  }
  return '/images/stadium.jpg';
};

/**
 * VideoCard Component
 * Displays a single video card with thumbnail, duration, and metadata
 */
const VideoCard: React.FC<VideoCardProps> = ({ 
  video, 
  onClick, 
  onDelete, 
  showDeleteButton = false 
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation(); // Prevent card click
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation(); // Prevent card click
    }
    setAnchorEl(null);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    handleMenuClose();
    if (onDelete) {
      onDelete(video.id);
    }
  };

  return (
    <StyledCard onClick={onClick}>
      <ThumbnailContainer>
        <Thumbnail
          src={getThumbnailUrl(video)}
          alt={video.title || "Video thumbnail"}
          onError={(e: any) => {
            // Fallback to lower quality or default image
            e.target.src = getFallbackThumbnailUrl(video);
          }}
        />
        
        <PlayOverlay className="play-overlay">
          <PlayButton>
            <PlayArrowIcon style={{ fontSize: "2rem" }} />
          </PlayButton>
        </PlayOverlay>

        {showDeleteButton && (
          <MenuButton onClick={handleMenuOpen} size="small">
            <MoreVertIcon style={{ fontSize: "1.2rem" }} />
          </MenuButton>
        )}

        {video.duration && (
          <DurationBadge>{formatDuration(video.duration)}</DurationBadge>
        )}
      </ThumbnailContainer>

      {/* Menu for actions */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleMenuClose()}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleDeleteClick}>
          <DeleteIcon style={{ marginRight: "8px", fontSize: "1.2rem" }} />
          Delete Video
        </MenuItem>
      </Menu>

      <VideoInfo>
        <VideoTitle>
          {video.title || "Match Video"}
        </VideoTitle>
        
        <VideoMeta>
          <span>{formatUploadedDate(video.uploadedAt)}</span>
          {video.uploadedBy && (
            <>
              <MetaDot>•</MetaDot>
              <span>{video.uploadedBy}</span>
            </>
          )}
        </VideoMeta>
      </VideoInfo>
    </StyledCard>
  );
};

export default VideoCard;
